import { Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'

/**
 * Shown when someone selects an action only an admin may take. The action
 * stays visible to everyone (so people know it exists and who to ask), and
 * this explains, in one sentence, why it did not happen.
 *
 * ``action`` completes "Only admins can ..." (e.g. "remove a property from an
 * HOA").
 */
export function AdminOnlyDialog({
  action,
  onClose,
}: {
  action: string | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  return (
    <Dialog open={action !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader className="items-center text-center">
          <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-[var(--green-bg)]">
            <Lock className="h-5 w-5 text-[var(--green)]" />
          </span>
          <DialogTitle className="text-[17px] font-normal text-[var(--ink)]">
            {t('admin.only.title', 'Ask an admin')}
          </DialogTitle>
          <DialogDescription className="text-[14px] leading-[1.5] text-[var(--ink-2)]">
            {t(
              'admin.only.body',
              'Only admins can {{action}}. Contact your admin to do this for you, or to give you access.',
              { action: action ?? '' },
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center">
          <Button onClick={onClose} className="min-w-[120px] cursor-pointer">
            {t('admin.only.ok', 'OK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
